import { useCallback, useEffect, useState } from "react";

import {
  addProgenyItemsToDocumentQueue,
  cancelScheduledDocumentQueue,
  getDocumentAdditionQueue,
  removeDocumentQueueItem,
  submitDocumentAdditionQueue,
  uploadCaseDocument,
  type CaseCheckRerunFields,
  type DocumentAdditionQueue,
} from "@/services/api/case-checking";
import { useTaskProgressDialogOptional } from "@/context/TaskProgressDialogContext";

import { waitForCaseCheckTask } from "./waitForCaseCheckTask";

type UseDocumentAdditionQueueOptions = {
  caseId: string | null;
  onSubmitComplete?: () => void;
  onTaskIdChange?: (taskId: string | null) => void;
};

export type DocumentQueueSubmitNowOptions = {
  /** When false, submit runs in the background without the global task progress dialog. */
  showTaskProgress?: boolean;
} & CaseCheckRerunFields;

function queueErrorMessage(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: unknown } } };
  const detail = ax.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export function useDocumentAdditionQueue({
  caseId,
  onSubmitComplete,
  onTaskIdChange,
}: UseDocumentAdditionQueueOptions) {
  const [queue, setQueue] = useState<DocumentAdditionQueue | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taskProgressDialog = useTaskProgressDialogOptional();

  const refreshQueue = useCallback(async () => {
    if (!caseId) {
      setQueue(null);
      return;
    }
    setIsLoading(true);
    try {
      const data = await getDocumentAdditionQueue(caseId);
      setQueue(data);
    } catch (err) {
      setError(queueErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    void refreshQueue();
  }, [refreshQueue]);

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!caseId) return;
      const list = Array.from(files);
      if (list.length === 0) return;
      setError(null);
      setIsUploading(true);
      try {
        for (const file of list) {
          await uploadCaseDocument(caseId, file, { queueAddition: true });
        }
        await refreshQueue();
      } catch (err) {
        setError(queueErrorMessage(err));
      } finally {
        setIsUploading(false);
      }
    },
    [caseId, refreshQueue],
  );

  const addProgenyDocumentIds = useCallback(
    async (progenyDocumentIds: string[], labels?: Record<string, string>) => {
      if (!caseId || progenyDocumentIds.length === 0) return;
      setError(null);
      try {
        const data = await addProgenyItemsToDocumentQueue(caseId, {
          progeny_document_ids: progenyDocumentIds,
          labels: labels ?? {},
        });
        setQueue(data);
      } catch (err) {
        setError(queueErrorMessage(err));
      }
    },
    [caseId],
  );

  const removeItem = useCallback(
    async (itemId: string) => {
      if (!caseId) return;
      setError(null);
      try {
        const data = await removeDocumentQueueItem(caseId, itemId);
        setQueue(data);
      } catch (err) {
        setError(queueErrorMessage(err));
      }
    },
    [caseId],
  );

  const submitNow = useCallback(
    async (options?: DocumentQueueSubmitNowOptions) => {
      if (!caseId) return;
      const { showTaskProgress = true, acknowledge_rerun, review_on_rerun, source_review_run_id } = options ?? {};
      setError(null);
      setIsSubmitting(true);
      try {
        const result = await submitDocumentAdditionQueue(caseId, "now", {
          acknowledge_rerun,
          review_on_rerun,
          source_review_run_id,
        });
        if (result.task_id) {
          onTaskIdChange?.(result.task_id);
          if (showTaskProgress) {
            taskProgressDialog?.openTaskProgress([result.task_id], "Re-running case from triage");
            await waitForCaseCheckTask(result.task_id);
            onTaskIdChange?.(null);
          }
        }
        await refreshQueue();
        onSubmitComplete?.();
      } catch (err) {
        setError(queueErrorMessage(err));
      } finally {
        setIsSubmitting(false);
      }
    },
    [caseId, onSubmitComplete, onTaskIdChange, refreshQueue, taskProgressDialog],
  );

  const scheduleTonight = useCallback(
    async (rerunFields: CaseCheckRerunFields = {}) => {
      if (!caseId) return;
      setError(null);
      setIsSubmitting(true);
      try {
        await submitDocumentAdditionQueue(caseId, "tonight", rerunFields);
        await refreshQueue();
      } catch (err) {
        setError(queueErrorMessage(err));
      } finally {
        setIsSubmitting(false);
      }
    },
    [caseId, refreshQueue],
  );

  const cancelSchedule = useCallback(async () => {
    if (!caseId) return;
    setError(null);
    try {
      const data = await cancelScheduledDocumentQueue(caseId);
      setQueue(data);
    } catch (err) {
      setError(queueErrorMessage(err));
    }
  }, [caseId]);

  const clearError = useCallback(() => setError(null), []);

  return {
    queue,
    refreshQueue,
    uploadFiles,
    addProgenyDocumentIds,
    removeItem,
    submitNow,
    scheduleTonight,
    cancelSchedule,
    isLoading,
    isUploading,
    isSubmitting,
    isBusy: isUploading || isSubmitting,
    error,
    clearError,
  };
}
